/**
 * Menu engine shared by dropdown-menu, context-menu and menubar.
 *
 * One implementation of everything a menu *surface* does once it is open:
 * roving focus, typeahead, activation, checkbox/radio state, groups, disabled
 * items, and submenus with hover intent and a pointer grace area. The owners
 * (`attachDropdownMenu`, `attachContextMenu`, `attachMenubar`) only decide
 * *when the surface opens and where*; this file decides how it behaves.
 *
 * Markup (slot names are `<prefix>-…`; `prefix` is `dropdown-menu`,
 * `context-menu` or `menubar`). Items are found by slot inside the content
 * element and belong to the nearest `-content` / `-sub-content` ancestor.
 *
 *   <prefix>-content div role=menu tabindex=-1 data-state=open|closed data-side data-align
 *   <prefix>-item div role=menuitem tabindex=-1 data-disabled? data-highlighted? data-value?
 *   <prefix>-checkbox-item div role=menuitemcheckbox aria-checked data-state=checked|unchecked data-disabled?
 *   <prefix>-radio-group div role=group data-value?
 *   <prefix>-radio-item div role=menuitemradio aria-checked data-state=checked|unchecked data-value
 *   <prefix>-group div role=group
 *   <prefix>-label div
 *   <prefix>-separator div role=separator
 *   <prefix>-shortcut span
 *   <prefix>-sub div
 *   <prefix>-sub-trigger div role=menuitem aria-haspopup=menu aria-expanded data-state=open|closed
 *   <prefix>-sub-content div role=menu data-state=open|closed data-side data-align
 *
 * An element with `data-menu-item-indicator` inside a checkbox/radio item is
 * shown only while the item is checked (Radix `ItemIndicator`).
 *
 * Item activation dispatches a cancelable, bubbling `menu-select` CustomEvent
 * on the item (`detail: MenuSelectDetail`). `preventDefault()` keeps the menu
 * open (Radix `onSelect`). Checkbox and radio items update their state first
 * and then dispatch the event, as Radix does.
 *
 * @module menu-core
 */
import { ensureId, mountFloating, readPlacement } from './_floating.js';
/** Slot names for a menu family, e.g. `menuSlots('dropdown-menu')`. */
export function menuSlots(prefix) {
    return {
        content: `${prefix}-content`,
        item: `${prefix}-item`,
        checkboxItem: `${prefix}-checkbox-item`,
        radioItem: `${prefix}-radio-item`,
        radioGroup: `${prefix}-radio-group`,
        group: `${prefix}-group`,
        label: `${prefix}-label`,
        separator: `${prefix}-separator`,
        sub: `${prefix}-sub`,
        subTrigger: `${prefix}-sub-trigger`,
        subContent: `${prefix}-sub-content`,
    };
}
const slotSelector = (...slots) => slots.map((slot) => `[data-slot="${slot}"]`).join(', ');
/** Text direction in effect at `from`: nearest `[dir]`, else the document's. */
export function resolveDirection(from) {
    const doc = from?.ownerDocument ?? document;
    const value = from?.closest('[dir]')?.getAttribute('dir') ?? doc.documentElement.dir;
    return value === 'rtl' ? 'rtl' : 'ltr';
}
/** True for items the author marked disabled. `data-disabled="false"` is enabled. */
export function isItemDisabled(item) {
    const data = item.getAttribute('data-disabled');
    if (data !== null && data !== 'false')
        return true;
    if (item.getAttribute('aria-disabled') === 'true')
        return true;
    return item.hasAttribute('disabled');
}
/** Ray-casting point-in-polygon. */
export function isPointInPolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        if (!a || !b)
            continue;
        const crosses = a.y > point.y !== b.y > point.y &&
            point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
        if (crosses)
            inside = !inside;
    }
    return inside;
}
/** Convex hull (Andrew's monotone chain). */
export function convexHull(points) {
    const sorted = [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
    if (sorted.length <= 1)
        return sorted;
    const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const build = (input) => {
        const out = [];
        for (const p of input) {
            for (;;) {
                const a = out[out.length - 2];
                const b = out[out.length - 1];
                if (a && b && cross(a, b, p) <= 0)
                    out.pop();
                else
                    break;
            }
            out.push(p);
        }
        out.pop();
        return out;
    };
    return [...build(sorted), ...build([...sorted].reverse())];
}
/**
 * The triangle-ish region a pointer may cross between a submenu trigger and
 * its content without the menu treating it as leaving: the exit point (bled
 * 5px back into the trigger) fanned out to the near and far edges of the
 * submenu content.
 */
export function graceArea(exit, content, side) {
    const rightSide = side === 'right';
    const bleed = rightSide ? -5 : 5;
    const near = rightSide ? content.left : content.right;
    const far = rightSide ? content.right : content.left;
    return convexHull([
        { x: exit.x + bleed, y: exit.y },
        { x: near, y: content.top },
        { x: far, y: content.top },
        { x: far, y: content.bottom },
        { x: near, y: content.bottom },
    ]);
}
/**
 * Where a navigation key moves focus within an ordered list of enabled items.
 *
 * Returns the item to focus, `null` when the key is navigation but there is
 * nowhere to go, and `undefined` when the key is not a navigation key.
 */
export function rovingTarget(items, current, key, options) {
    const { orientation, dir, loop } = options;
    const forward = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const backward = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    const vertical = orientation !== 'horizontal';
    const horizontal = orientation !== 'vertical';
    const isNext = (vertical && key === 'ArrowDown') || (horizontal && key === forward);
    const isPrev = (vertical && key === 'ArrowUp') || (horizontal && key === backward);
    if (key === 'Home' || key === 'PageUp')
        return items[0] ?? null;
    if (key === 'End' || key === 'PageDown')
        return items[items.length - 1] ?? null;
    if (!isNext && !isPrev)
        return undefined;
    if (items.length === 0)
        return null;
    const index = current ? items.indexOf(current) : -1;
    if (index === -1)
        return (isNext ? items[0] : items[items.length - 1]) ?? null;
    const step = index + (isNext ? 1 : -1);
    if (step < 0 || step >= items.length) {
        if (!loop)
            return null;
        return (isNext ? items[0] : items[items.length - 1]) ?? null;
    }
    return items[step] ?? null;
}
/** Writes the roving tabindex: `active` is tabbable, the rest are not. */
export function setRovingTabindex(items, active) {
    for (const item of items)
        item.tabIndex = item === active ? 0 : -1;
}
const GRACE_MS = 300;
function withoutEdgeKey(options) {
    const { onEdgeKey: _edge, ...rest } = options;
    return rest;
}
export function attachMenuEngine(options) {
    return createLevel(options, null);
}
function createLevel(options, parent) {
    const { content, prefix } = options;
    const slots = menuSlots(prefix);
    const doc = content.ownerDocument;
    const itemSelector = slotSelector(slots.item, slots.checkboxItem, slots.radioItem, slots.subTrigger);
    const contentSelector = slotSelector(slots.content, slots.subContent);
    const subSelector = slotSelector(slots.sub);
    const subContentSelector = slotSelector(slots.subContent);
    const orientation = options.orientation ?? 'vertical';
    const loop = options.loop ?? false;
    const typeaheadTimeout = options.typeaheadTimeout ?? 1000;
    const subOpenDelay = options.subOpenDelay ?? 100;
    const subs = new Map();
    let buffer = '';
    let bufferTimer;
    let graceIntent = null;
    let graceTimer;
    let lastPointerX = 0;
    let pointerDir = 'right';
    let destroyed = false;
    const dir = () => options.dir?.() ?? parent?.dir() ?? 'ltr';
    const allItems = () => Array.from(content.querySelectorAll(itemSelector)).filter((element) => element.closest(contentSelector) === content);
    const enabledItems = () => allItems().filter((item) => !isItemDisabled(item));
    const itemOf = (target) => {
        if (!(target instanceof Element))
            return null;
        const item = target.closest(itemSelector);
        return item && item.closest(contentSelector) === content ? item : null;
    };
    const kindOf = (item) => {
        const slot = item.getAttribute('data-slot');
        if (slot === slots.checkboxItem)
            return 'checkbox';
        if (slot === slots.radioItem)
            return 'radio';
        if (slot === slots.subTrigger)
            return 'sub';
        return 'item';
    };
    const textOf = (item) => (item.getAttribute('data-text-value') ?? item.textContent ?? '').trim();
    const activeItem = () => itemOf(doc.activeElement);
    const radioGroupOf = (item) => {
        const group = item.closest(slotSelector(slots.radioGroup));
        return group && group.closest(contentSelector) === content ? group : null;
    };
    // --- markup sync --------------------------------------------------------
    function writeChecked(item, checked) {
        item.setAttribute('aria-checked', String(checked));
        item.setAttribute('data-state', checked ? 'checked' : 'unchecked');
        for (const indicator of item.querySelectorAll('[data-menu-item-indicator]')) {
            indicator.hidden = !checked;
            indicator.setAttribute('data-state', checked ? 'checked' : 'unchecked');
        }
    }
    function syncItem(item) {
        const kind = kindOf(item);
        item.setAttribute('role', kind === 'checkbox' ? 'menuitemcheckbox' : kind === 'radio' ? 'menuitemradio' : 'menuitem');
        if (!item.hasAttribute('tabindex'))
            item.tabIndex = -1;
        if (isItemDisabled(item)) {
            item.setAttribute('data-disabled', '');
            item.setAttribute('aria-disabled', 'true');
        }
        else {
            item.removeAttribute('data-disabled');
            item.removeAttribute('aria-disabled');
        }
        if (kind === 'checkbox') {
            writeChecked(item, item.getAttribute('aria-checked') === 'true' ||
                item.getAttribute('data-state') === 'checked');
        }
        else if (kind === 'radio') {
            const group = radioGroupOf(item);
            const groupValue = group?.getAttribute('data-value') ?? null;
            const value = item.getAttribute('data-value');
            const checked = groupValue !== null && value !== null
                ? groupValue === value
                : item.getAttribute('aria-checked') === 'true' ||
                    item.getAttribute('data-state') === 'checked';
            writeChecked(item, checked);
        }
        else if (kind === 'sub') {
            item.setAttribute('aria-haspopup', 'menu');
            const open = subs.get(item)?.open ?? false;
            item.setAttribute('aria-expanded', String(open));
            item.setAttribute('data-state', open ? 'open' : 'closed');
        }
    }
    function refresh() {
        content.setAttribute('role', 'menu');
        content.setAttribute('aria-orientation', orientation);
        if (!content.hasAttribute('tabindex'))
            content.tabIndex = -1;
        const items = allItems();
        for (const item of items)
            syncItem(item);
        const owned = (element) => element.closest(contentSelector) === content;
        for (const group of content.querySelectorAll(slotSelector(slots.group, slots.radioGroup))) {
            if (!owned(group))
                continue;
            group.setAttribute('role', 'group');
            const label = Array.from(group.querySelectorAll(slotSelector(slots.label))).find((candidate) => candidate.closest(slotSelector(slots.group, slots.radioGroup)) === group);
            if (label && !group.hasAttribute('aria-labelledby')) {
                group.setAttribute('aria-labelledby', ensureId(label, 'menu-label'));
            }
        }
        for (const separator of content.querySelectorAll(slotSelector(slots.separator))) {
            if (!owned(separator))
                continue;
            separator.setAttribute('role', 'separator');
            separator.setAttribute('aria-orientation', 'horizontal');
        }
        const focused = activeItem();
        setRoving(focused && !isItemDisabled(focused) ? focused : null);
    }
    function setRoving(active) {
        const items = allItems();
        const tabbable = active ?? items.find((item) => !isItemDisabled(item)) ?? null;
        setRovingTabindex(items, tabbable);
        for (const item of items) {
            if (item === active)
                item.setAttribute('data-highlighted', '');
            else
                item.removeAttribute('data-highlighted');
        }
    }
    function highlightItem(item, scroll) {
        setRoving(item);
        item.focus({ preventScroll: true });
        if (scroll)
            item.scrollIntoView?.({ block: 'nearest' });
    }
    // --- selection ----------------------------------------------------------
    function activate(item) {
        const kind = kindOf(item);
        if (kind === 'sub') {
            openSub(item, false);
            return;
        }
        if (kind === 'checkbox') {
            const next = item.getAttribute('aria-checked') !== 'true';
            writeChecked(item, next);
            options.onCheckedChange?.(item, next);
        }
        else if (kind === 'radio') {
            const group = radioGroupOf(item);
            const value = item.getAttribute('data-value') ?? textOf(item);
            if (group) {
                group.setAttribute('data-value', value);
                for (const sibling of allItems()) {
                    if (kindOf(sibling) === 'radio' && radioGroupOf(sibling) === group) {
                        writeChecked(sibling, sibling === item);
                    }
                }
                options.onValueChange?.(group, value, item);
            }
            else {
                writeChecked(item, true);
            }
        }
        const detail = {
            item,
            value: item.getAttribute('data-value'),
            text: textOf(item),
        };
        const event = new CustomEvent('menu-select', {
            bubbles: true,
            cancelable: true,
            detail,
        });
        item.dispatchEvent(event);
        options.onSelect?.(detail, event);
        if (!event.defaultPrevented && options.closeOnSelect !== false)
            options.requestClose('select');
    }
    // --- typeahead ----------------------------------------------------------
    function typeahead(character) {
        buffer += character;
        clearTimeout(bufferTimer);
        bufferTimer = setTimeout(() => {
            buffer = '';
            bufferTimer = undefined;
        }, typeaheadTimeout);
        const enabled = enabledItems();
        const current = activeItem();
        const currentIndex = current ? enabled.indexOf(current) : -1;
        const repeated = buffer.length > 1 && Array.from(buffer).every((c) => c === buffer[0]);
        const query = (repeated ? buffer.slice(0, 1) : buffer).toLowerCase();
        const start = Math.max(currentIndex, 0);
        let order = enabled.map((_, i) => (start + i) % enabled.length);
        if (query.length === 1)
            order = order.filter((i) => i !== currentIndex);
        const hit = order.find((i) => {
            const item = enabled[i];
            return item ? textOf(item).toLowerCase().startsWith(query) : false;
        });
        const match = hit === undefined ? undefined : enabled[hit];
        if (match && match !== current)
            highlightItem(match, true);
    }
    // --- pointer grace ------------------------------------------------------
    function movingToSubmenu(event) {
        if (!graceIntent)
            return false;
        return (pointerDir === graceIntent.side &&
            isPointInPolygon({ x: event.clientX, y: event.clientY }, graceIntent.area));
    }
    function setGrace(event, state) {
        const rect = state.content.getBoundingClientRect();
        const side = state.content.getAttribute('data-side') === 'left' ? 'left' : 'right';
        graceIntent = {
            area: graceArea({ x: event.clientX, y: event.clientY }, rect, side),
            side,
        };
        clearTimeout(graceTimer);
        graceTimer = setTimeout(() => {
            graceIntent = null;
            graceTimer = undefined;
        }, GRACE_MS);
    }
    // --- submenus -----------------------------------------------------------
    function subStateFor(trigger) {
        const existing = subs.get(trigger);
        if (existing)
            return existing;
        const subRoot = trigger.closest(subSelector);
        if (!subRoot)
            return null;
        const subContent = Array.from(subRoot.querySelectorAll(subContentSelector)).find((candidate) => candidate.closest(subSelector) === subRoot);
        if (!subContent)
            return null;
        const state = {
            trigger,
            content: subContent,
            open: false,
            floating: null,
            engine: null,
            openTimer: undefined,
        };
        subs.set(trigger, state);
        return state;
    }
    function openSub(trigger, focus) {
        if (isItemDisabled(trigger))
            return;
        const state = subStateFor(trigger);
        if (!state)
            return;
        clearTimeout(state.openTimer);
        state.openTimer = undefined;
        if (state.open) {
            if (focus)
                state.engine?.highlight('first');
            return;
        }
        teardownSub(state);
        state.open = true;
        const subContent = state.content;
        subContent.setAttribute('data-state', 'open');
        trigger.setAttribute('aria-expanded', 'true');
        trigger.setAttribute('data-state', 'open');
        trigger.setAttribute('aria-controls', ensureId(subContent, 'menu-sub'));
        subContent.setAttribute('aria-labelledby', ensureId(trigger, 'menu-sub-trigger'));
        state.floating = mountFloating({
            content: subContent,
            anchor: trigger,
            modal: false,
            initialFocus: false,
            isInside: (node) => trigger.contains(node) || levelContains(node),
            onDismiss: (reason) => {
                if (reason === 'escape')
                    closeSub(state, true);
                else
                    options.requestClose('outside');
            },
            placement: readPlacement(subContent, {}, {
                side: dir() === 'rtl' ? 'left' : 'right',
                align: 'start',
                sideOffset: 0,
                alignOffset: options.subAlignOffset ?? -4,
                collisionPadding: 8,
                rtl: dir() === 'rtl',
            }),
        });
        state.engine = createLevel({ ...withoutEdgeKey(options), content: subContent }, {
            dir,
            contains: levelContains,
            closeSelf: (restoreFocus) => closeSub(state, restoreFocus),
        });
        state.engine.refresh();
        if (focus)
            state.engine.highlight('first');
    }
    function closeSub(state, restoreFocus) {
        clearTimeout(state.openTimer);
        state.openTimer = undefined;
        if (!state.open)
            return;
        state.open = false;
        state.engine?.closeSubmenus();
        state.content.setAttribute('data-state', 'closed');
        state.trigger.setAttribute('aria-expanded', 'false');
        state.trigger.setAttribute('data-state', 'closed');
        if (restoreFocus)
            highlightItem(state.trigger, false);
        state.floating?.close(() => teardownSub(state));
    }
    function teardownSub(state) {
        if (state.open)
            return;
        state.engine?.destroy();
        state.engine = null;
        state.floating?.destroy();
        state.floating = null;
        state.content.hidden = true;
    }
    function levelContains(node) {
        return content.contains(node) || (parent?.contains(node) ?? false);
    }
    function closeOtherSubs(keep) {
        for (const state of subs.values()) {
            if (state.open && state.trigger !== keep)
                closeSub(state, false);
        }
    }
    // --- events -------------------------------------------------------------
    const subOpenKey = () => {
        if (orientation === 'horizontal')
            return 'ArrowDown';
        return dir() === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    };
    const subCloseKey = () => {
        if (orientation === 'horizontal')
            return 'ArrowUp';
        return dir() === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    };
    function onKeydown(event) {
        if (event.defaultPrevented)
            return;
        const target = event.target;
        if (!(target instanceof Element) || target.closest(contentSelector) !== content)
            return;
        const key = event.key;
        if (key === 'Tab') {
            if (options.tabBehavior === 'close')
                options.requestClose('tab');
            else
                event.preventDefault();
            return;
        }
        const item = itemOf(target);
        const typing = buffer !== '';
        if (item && (key === 'Enter' || (key === ' ' && !typing))) {
            event.preventDefault();
            if (isItemDisabled(item))
                return;
            if (kindOf(item) === 'sub')
                openSub(item, 'first');
            else
                item.click();
            return;
        }
        if (item && kindOf(item) === 'sub' && key === subOpenKey()) {
            event.preventDefault();
            if (!isItemDisabled(item))
                openSub(item, 'first');
            return;
        }
        if (parent && key === subCloseKey()) {
            event.preventDefault();
            event.stopPropagation();
            parent.closeSelf(true);
            return;
        }
        if (!parent && options.onEdgeKey && orientation === 'vertical') {
            const forward = dir() === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
            const backward = dir() === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
            if (key === forward || key === backward) {
                event.preventDefault();
                options.onEdgeKey(key === forward ? 'next' : 'previous', event);
                return;
            }
        }
        const next = rovingTarget(enabledItems(), activeItem(), key, { orientation, dir: dir(), loop });
        if (next !== undefined) {
            event.preventDefault();
            if (next)
                highlightItem(next, true);
            return;
        }
        if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            typeahead(key);
        }
    }
    function onPointerMove(event) {
        if (event.clientX !== lastPointerX) {
            pointerDir = event.clientX > lastPointerX ? 'right' : 'left';
            lastPointerX = event.clientX;
        }
        if (event.pointerType === 'touch')
            return;
        const item = itemOf(event.target);
        if (!item)
            return;
        if (isItemDisabled(item)) {
            leaveItem(item, event);
            return;
        }
        if (movingToSubmenu(event))
            return;
        if (item !== doc.activeElement)
            highlightItem(item, false);
        if (kindOf(item) === 'sub') {
            const state = subStateFor(item);
            if (state && !state.open && !state.openTimer) {
                state.openTimer = setTimeout(() => {
                    state.openTimer = undefined;
                    openSub(item, false);
                }, subOpenDelay);
            }
        }
    }
    function leaveItem(item, event) {
        if (movingToSubmenu(event))
            return;
        if (kindOf(item) === 'sub' && subs.get(item)?.open)
            return;
        setRoving(null);
        content.focus({ preventScroll: true });
    }
    function onPointerOut(event) {
        if (event.pointerType === 'touch')
            return;
        const item = itemOf(event.target);
        if (!item)
            return;
        if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget))
            return;
        if (kindOf(item) === 'sub') {
            const state = subs.get(item);
            if (state?.openTimer) {
                clearTimeout(state.openTimer);
                state.openTimer = undefined;
            }
            if (state?.open)
                setGrace(event, state);
        }
        leaveItem(item, event);
    }
    function onClick(event) {
        const item = itemOf(event.target);
        if (!item)
            return;
        if (isItemDisabled(item)) {
            event.preventDefault();
            return;
        }
        activate(item);
    }
    function onFocusIn(event) {
        const item = itemOf(event.target);
        if (!item || isItemDisabled(item))
            return;
        setRoving(item);
        closeOtherSubs(item);
    }
    function onFocusOut(event) {
        const item = itemOf(event.target);
        if (item)
            item.removeAttribute('data-highlighted');
    }
    content.addEventListener('keydown', onKeydown);
    content.addEventListener('pointermove', onPointerMove);
    content.addEventListener('pointerout', onPointerOut);
    content.addEventListener('click', onClick);
    content.addEventListener('focusin', onFocusIn);
    content.addEventListener('focusout', onFocusOut);
    refresh();
    return {
        content,
        items: allItems,
        get highlighted() {
            return activeItem();
        },
        highlight(target) {
            const item = target === 'first' ? enabledItems()[0] : target === 'last' ? enabledItems().at(-1) : target;
            if (item)
                highlightItem(item, true);
            else
                content.focus({ preventScroll: true });
        },
        contains: levelContains,
        refresh,
        closeSubmenus() {
            for (const state of subs.values()) {
                if (state.open)
                    closeSub(state, false);
            }
        },
        destroy() {
            if (destroyed)
                return;
            destroyed = true;
            content.removeEventListener('keydown', onKeydown);
            content.removeEventListener('pointermove', onPointerMove);
            content.removeEventListener('pointerout', onPointerOut);
            content.removeEventListener('click', onClick);
            content.removeEventListener('focusin', onFocusIn);
            content.removeEventListener('focusout', onFocusOut);
            clearTimeout(bufferTimer);
            clearTimeout(graceTimer);
            for (const state of subs.values()) {
                clearTimeout(state.openTimer);
                state.open = false;
                state.content.setAttribute('data-state', 'closed');
                state.trigger.setAttribute('aria-expanded', 'false');
                state.trigger.setAttribute('data-state', 'closed');
                teardownSub(state);
            }
            subs.clear();
        },
    };
}
